import { ReactNode, useEffect, useRef, useState } from 'react'

import { ContactService, IContactForm } from '../../../utilities/salesForceForm'
import { PhnxButton } from '../../01-atoms/Button/Button'
import styles from './Advisor.module.css'

export type SpecialtiesProps = { title: string; href: string }

export type LocationProps = {
  address?: string[]
  mapHref?: string
  phone?: string
  component?: string
  office?: any
}

export type AdvisorProps = {
  title: string
  role: string
  link?: string
  avatar: React.ReactNode
  biography?: React.ReactNode
  specialties?: SpecialtiesProps[]
  officelocations?: LocationProps[]
  articles?: ReactNode[] | null
  salesforceid: string
}

export const PhnxAdvisor: React.FC<AdvisorProps> = ({
  title,
  role,
  link = '/advisers/search',
  avatar,
  biography,
  specialties,
  officelocations,
  articles,
  salesforceid,
}) => {
  const [showContact, setShowContact] = useState<boolean>(false)
  const callbackInputRef = useRef<HTMLInputElement>(null)
  const hasSubmittedRef = useRef(false)
  const [formState, setFormState] = useState<IContactForm>({} as IContactForm)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [utmSource, setUtmSource] = useState<string>('')
  const [utmMedium, setUtmMedium] = useState<string>('')
  const [utmContent, setUtmContent] = useState<string>('')
  const [utmTerm, setUtmTerm] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const location = process.env.NEXT_PUBLIC_NEXT_LOCATION || 'au'

  const introSentence =
    location === 'au'
      ? 'Please fill out the form below and we will be in touch, or phone us on 1300 856 065.'
      : 'Please fill out the form below and we will be in touch.'
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      setUtmSource(urlParams.get('utm_source') || '')
      setUtmMedium(urlParams.get('utm_medium') || '')
      setUtmContent(urlParams.get('utm_content') || '')
      setUtmTerm(urlParams.get('utm_term') || '')
    }
  }, [])

  const updateContact = () => {
    setShowContact(!showContact)

    // log out the form state, loading and error
    console.warn('formState', formState)
    console.warn('loading', loading)
    console.warn('error', error)
  }

  // if url parms has requestCallback=true then show the contact form
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('requestCallback') === 'true') {
      setShowContact(true)
    }
  }, [])

  useEffect(() => {
    // When the contact section is shown, focus the form element
    if (showContact && callbackInputRef.current) {
      callbackInputRef.current.focus()
    }

    // Listen for keydown events to close contact form on Esc key press
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowContact(false)
      }
    }

    // Add event listener on mount
    document.addEventListener('keydown', handleKeyDown)

    // Clean up event listener on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showContact])

  const validateForm = (form: any) => {
    const errors: string[] = []
    // regex for phone number must be numbers
    const phoneRegex = /^[0-9]+$/

    if (!phoneRegex.test(form.phone) && form.phone) {
      errors.push('Phone number must be numbers only')
    }

    // regex for email

    return errors
  }

  const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return

    setError('')
    setLoading(true)
    setIsSubmitting(true)

    const form = document.getElementById('request-callback') as HTMLFormElement
    const inputs = Array.from(form.getElementsByTagName('input'))
    const textareas = Array.from(form.getElementsByTagName('textarea'))
    const selects = Array.from(form.getElementsByTagName('select'))
    const allInputs = [...inputs, ...textareas, ...selects]

    const formData: { [key: string]: string } = {}

    for (let i = 0; i < allInputs.length; i++) {
      const input = allInputs[i]
      formData[input.name] = input.value
    }

    // put in the form state
    const submissionEndpoint = process.env.NEXT_PUBLIC_LEADS_FORM_ENDPOINT + 'lead/submit'

    const castedFormData = formData as any

    const adviserFirstname = title.split(' ')[0]
    const adviserLastname = title.split(' ')[1]

    let adviserInfo = ''
    if (officelocations && officelocations.length > 0) {
      // @ts-ignore
      adviserInfo = `${adviserFirstname},${adviserLastname},${role},${officelocations[0]?.address[0] ?? ''},${salesforceid}`
    } else {
      adviserInfo = `${adviserFirstname},${adviserLastname},${role},No office location,${salesforceid} `
    }

    castedFormData.leadSource = process.env.NEXT_PUBLIC_CRM_FORM_FIELD_VALUE_LEAD_SOURCE
    castedFormData.leadType = 'Individual'
    castedFormData.givenName = castedFormData.firstname
    castedFormData.surname = castedFormData.lastname
    castedFormData.fullName = `${castedFormData.firstname} ${castedFormData.lastname}`
    castedFormData.email = castedFormData.email
    castedFormData.phone = castedFormData.phone
    castedFormData.zip = castedFormData.postcode
    castedFormData.state = castedFormData.state
    castedFormData.contactMethod = castedFormData['contact-preference']
    castedFormData.adviserInfo = adviserInfo
    castedFormData.existingClient = castedFormData['existing-customer'] === 'yes'
    castedFormData.message = castedFormData.message
    castedFormData.formSubmitted = true
    castedFormData.serviceType = castedFormData.service || 'None'
    castedFormData.industry = 'None'
    castedFormData.enquiryType = 'Request a callback'
    castedFormData.endpoint = process.env.NEXT_PUBLIC_CRM_FORM_FIELD_VALUE_ENDPOINT
    castedFormData.retUrl = process.env.NEXT_PUBLIC_CRM_FORM_FIELD_VALUE_RETURL
    castedFormData.captcha_settings = { keyname: 'Google_Recaptcha', fallback: 'true' }

    // merge into setUpForm and add utm params
    setFormState({
      ...castedFormData,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_content: utmContent,
      utm_term: utmTerm,
    })

    const errors = validateForm(castedFormData)
    if (errors.length > 0) {
      setError('Please correct the following errors and try again: ' + errors)
      setLoading(false)
      console.error('Validation errors:', errors)
      return
    }

    // Check honeypot fields and if they are filled, redirect to thank you page
    const honeypotFields = ['name_first', 'email_address']
    for (const field of honeypotFields) {
      if (formData[field] && formData[field].length > 0) {
        window.location.href = '/complete'
        setLoading(false)
        return
      }
    }

    const contactService = new ContactService(submissionEndpoint)
    try {
      const salesforceForm = contactService.mapFormToSalesforce(formData as IContactForm | any)
      await contactService.submitForm(salesforceForm, submissionEndpoint)
      hasSubmittedRef.current = true
      setLoading(false)
      window.location.href = '/adviser-thankyou'
    } catch (err) {
      setLoading(false)
      setError('An error occurred while submitting the form. Please try again.')
      console.error('Error submitting form:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={styles['advisor']}>
      <div className={styles['advisor-headline']}>
        <div className={` container`}>
          <div>
            <h1 className={styles['title']}>{title}</h1>
            <h3 className={styles['role']}>{role}</h3>
          </div>
          <div className={styles['link']}>
            <PhnxButton
              variant="quaternary"
              label="Search other advisers"
              type="link"
              href={link}
              size="small"
            />
          </div>
        </div>
      </div>
      <div className={`${styles['advisor-container']} container`}>
        <div className={styles['advisor-image']}>
          {avatar}
          <PhnxButton variant="primary" label="Contact us" type="button" onClick={updateContact} />
        </div>
        <div className={styles['advisor-details']}>
          <div className={styles['content-border']}>
            <h2>Biography</h2>
            <div>{biography}</div>
          </div>
          {specialties && (
            <div className={styles['content-border']}>
              <h2>My Specialties</h2>
              {specialties.map((item, index) => (
                <span key={index}>
                  <a href={item.href} className={styles.specailty} key={`specialty-${index}`}>
                    {item.title}
                  </a>
                  {index < specialties.length - 1 && <span>, </span>}
                </span>
              ))}
            </div>
          )}
          {officelocations && (
            <div className={styles['advisor-locations']}>
              <h5>{officelocations.length == 1 ? 'Office Location' : 'Office Locations'}</h5>
              <div className={styles.locations}>
                {officelocations &&
                  officelocations.map((item, index) => (
                    <div key={`location-${index}`}>
                      {item?.address && (
                        <>
                          {item?.address &&
                            item?.address?.map((line, linesindex) => (
                              <div key={`address-${index}-${linesindex}`}>{line}</div>
                            ))}

                          {/* if advance office the address is stored in office[0].address */}
                          {item?.component === 'advanceOffice' && (
                            <div>{item?.office[0]?.address}</div>
                          )}
                        </>
                      )}
                      {item?.mapHref && (
                        <a
                          className={styles.locationAdd}
                          href={item.mapHref}
                          key={`mapHref-${index}`}
                        >
                          View on Map
                        </a>
                      )}
                      {item?.phone && (
                        <p className={styles['advisor-phone']}>
                          Phone: <a href={`tel:${item.phone}`}>{item.phone}</a>
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {articles && articles.length > 0 && (
        <div className={`${styles['advisor-headline']} ${styles['articles']}`}>
          <h2 className={styles['articles-header']}>Articles by me</h2>
          <div className={`${styles['advisor-articles']} container`}>
            {articles.map((item, index) => (
              <div key={`article-${index}`}>{item}</div>
            ))}
          </div>
        </div>
      )}

      <div className={` ${styles[showContact ? 'show' : '']} ${styles['callback-container']}`}>
        <div className={styles['callback-tab']}>
          <div className={styles['callback-x']}>
            <button onClick={updateContact}>X</button>
          </div>
          <div className={styles['callback-title']}>
            <p>{introSentence}</p>
          </div>
          <form onSubmit={submitForm} id="request-callback">
            <div className={`${styles['callback-form']} request-callback_form-wrapper`}>
              <label htmlFor="firstname">First name *</label>
              <input type="text" id="firstname" name="firstname" ref={callbackInputRef} required />
              <label htmlFor="lastname">Last name *</label>
              <input type="text" id="lastname" name="lastname" required />
              <label htmlFor="email">Email *</label>
              <input type="email" id="email" name="email" required />
              <label htmlFor="phone">Phone *</label>
              <input type="tel" id="phone" name="phone" required />
              <label htmlFor="postcode">Postcode *</label>
              <input type="text" id="postcode" name="postcode" required />
              {process.env.NEXT_PUBLIC_NEXT_LOCATION !== 'en-nz' && (
                <>
                  <label htmlFor="state">State</label>
                  <select id="state" name="state" required className={styles['callback-select']}>
                    <option value="">Select a state</option>
                    <option value="NSW">New South Wales</option>
                    <option value="VIC">Victoria</option>
                    <option value="QLD">Queensland</option>
                    <option value="SA">South Australia</option>
                    <option value="WA">Western Australia</option>
                    <option value="TAS">Tasmania</option>
                    <option value="NT">Northern Territory</option>
                    <option value="ACT">Australian Capital Territory</option>
                  </select>
                </>
              )}
              <div>
                <fieldset>
                  <legend>Are you an existing customer? *</legend>
                  <div className={styles['callback-radiolayout']}>
                    <div className={`${styles['callback-radiolayout']} radio-group`}>
                      <input
                        id="yes-existing-client"
                        name="existing-customer"
                        type="radio"
                        value="yes"
                        required
                      />
                      <label htmlFor="yes-existing-client" className={styles['custom-radio']}>
                        Yes
                      </label>
                    </div>
                    <div className={styles['callback-radiolayout']}>
                      <input
                        id="existing-no"
                        name="existing-customer"
                        type="radio"
                        value="no"
                        required
                      />
                      <label htmlFor="existing-no" className={styles['custom-radio']}>
                        No
                      </label>
                    </div>
                  </div>
                </fieldset>
                <fieldset>
                  <legend>How should we contact you? *</legend>
                  <div className={styles['callback-radiolayout']}>
                    <div className={styles['callback-radiolayout']}>
                      <input
                        id="contact-email"
                        name="contact-preference"
                        type="radio"
                        value="email"
                        required
                      />

                      <label htmlFor="contact-email" className={styles['custom-radio']}>
                        Email
                      </label>
                    </div>
                    <div className={styles['callback-radiolayout']}>
                      <input
                        id="contact-phone"
                        name="contact-preference"
                        type="radio"
                        value="phone"
                        required
                      />

                      <label htmlFor="contact-phone" className={styles['custom-radio']}>
                        Phone
                      </label>
                    </div>
                  </div>
                </fieldset>
              </div>

              <label htmlFor="service">Service Type *</label>
              <select id="service" name="service" required className={styles['callback-select']}>
                <option value="">All / Any</option>
                {specialties &&
                  specialties.map((item, index) => (
                    <option value={item.title} key={`specialty-${index}`}>
                      {item.title}
                    </option>
                  ))}
              </select>
              <label htmlFor="message">Message</label>
              <textarea id="message" name="message" placeholder="Message here" />

              <PhnxButton
                variant="primary"
                label={isSubmitting ? 'Submitting...' : 'Submit'}
                type="submit"
                disabled={isSubmitting || hasSubmittedRef.current}
              />
            </div>
            <div className={styles['callback-consent']}>
              <p>
                By signing up, you agree to the Findex{' '}
                <a href="/disclaimers/privacy-policy">Privacy Policy</a>, and consent to receiving
                information from Findex, which you may unsubscribe from at any time.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
